import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
} from "@chakra-ui/react";
import { TimeIcon } from "@chakra-ui/icons";
import axios from "axios";
import { normalizeEvaluationStatus } from "./evaluationStatus";

const StartEvaluation = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [evaluationDetails, setEvaluationDetails] = useState(null);

  useEffect(() => {
    const fetchEvaluationData = async () => {
      try {
        setLoading(true);
        // Get evaluation status
        const statusResponse = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/status`
        );
        
        const normalizedStatus = normalizeEvaluationStatus(statusResponse.data);
        setEvaluationStatus(normalizedStatus);

        if (normalizedStatus.isMissing || normalizedStatus.isBlocked) {
          setLoading(false);
          return;
        }

        // Get evaluation details using the new API
        const detailsResponse = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluation/${uuid}/detail`
        );
        
        setEvaluationDetails(detailsResponse.data);

        // Persist STT language for later use in QuestionPage
        try {
          const rawLang = detailsResponse?.data?.language;
          let sttLang = 'fr-FR';
          if (typeof rawLang === 'string') {
            const l = rawLang.toLowerCase();
            if (l.includes('angl')) sttLang = 'en-US';
            else if (l.includes('fran')) sttLang = 'fr-FR';
          }
          localStorage.setItem(`sttLang_${uuid}`, sttLang);
        } catch {}
        setLoading(false);
      } catch (err) {
        console.error('Error fetching evaluation data:', err);
        setError('Failed to fetch evaluation data. Please try again later.');
        setLoading(false);
      }
    };

    fetchEvaluationData();
  }, [uuid]);

  useEffect(() => {
    // If a saved question exists, redirect to it
    const savedQuestion = localStorage.getItem(`currentQuestion_${uuid}`);
    if (evaluationStatus?.canResume && savedQuestion) {
      navigate(`/evaluate/${uuid}/q/${savedQuestion}`);
    }
  }, [evaluationStatus, uuid, navigate]);

  const handleStartAssessment = () => {
    // Clear any previous evaluation data
    localStorage.removeItem(`answers_${uuid}`);
    localStorage.removeItem(`currentQuestion_${uuid}`);
    localStorage.removeItem(`completedGroups_${uuid}`);
    localStorage.removeItem(`startTime_${uuid}`);
    localStorage.removeItem(`furthestQuestion_${uuid}`);

    // Navigate to the group summary screen
    navigate(`/evaluate/${uuid}/summary`);
  };

  // Calculate total time
  const calculateTotalTime = () => {
    if (!evaluationDetails || !evaluationDetails.technologies) return 0;
    return evaluationDetails.technologies.reduce((total, tech) => total + (tech.total_response_time || 0), 0);
  };

  // Calculate total questions
  const calculateTotalQuestions = () => {
    if (!evaluationDetails || !evaluationDetails.technologies) return 0;
    return evaluationDetails.technologies.reduce((total, tech) => total + (tech.question_count || 0), 0);
  };

  // Format time (seconds to minutes:seconds)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.600">Chargement des données de l'évaluation...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Une erreur est survenue
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                {error}
              </AlertDescription>
              <Button colorScheme="red" onClick={() => window.location.reload()}>
                Réessayer
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Handle case where evaluation doesn't exist
  if (evaluationStatus?.isMissing) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="warning"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Évaluation non trouvée
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                Aucune évaluation n'existe avec cet identifiant.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Handle completed evaluation
  if (evaluationStatus?.isCompleted) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Évaluation déjà terminée
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                Vous avez déjà terminé cette évaluation.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  if (evaluationStatus?.isSubmitted) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="220px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Évaluation déjà soumise
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                Vos réponses ont déjà été soumises et sont en cours de traitement.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Default case - show evaluation summary by technology
  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.lg">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={8} shadow="sm">
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color="black" display="flex" alignItems="center" gap={2}>
              📋 Résumé de l'évaluation
            </Heading>
            
            <Text fontSize="md" color="gray.700">
              Vous êtes sur le point de commencer votre évaluation technique. Voici le détail des technologies évaluées :
            </Text>
            
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Technologie</Th>
                    <Th isNumeric>Questions</Th>
                    <Th isNumeric>Temps</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {evaluationDetails?.technologies.map((tech, index) => (
                    <Tr key={index}>
                      <Td fontWeight="medium">{tech.technology}</Td>
                      <Td isNumeric>{tech.question_count}</Td>
                      <Td isNumeric>
                        <HStack justify="flex-end" spacing={1}>
                          <TimeIcon boxSize={3} color="gray.500" />
                          <Text sx={{ marginBottom: "0 !important" }}>
                            {formatTime(tech.total_response_time)}
                          </Text>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            
            <Divider />
            
            <HStack justify="space-between" bg="purple.50" p={4} borderRadius="md">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">Nombre total de questions</Text>
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  {calculateTotalQuestions()}
                </Text>
              </VStack>
              <VStack align="end" spacing={1}>
                <Text fontSize="sm" color="gray.600">Temps total estimé</Text>
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  {formatTime(calculateTotalTime())}
                </Text>
              </VStack>
            </HStack>
            
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontSize="sm" mt="15px">
                  Assurez-vous d'avoir suffisamment de temps disponible pour terminer chaque section de l'évaluation. 
                  Une fois la thématique commencée, l'évaluation sur ce thème ne peut pas être mise en pause.
                </Text>
              </Box>
            </Alert>
            
            <Button 
              bg="rgb(37, 87, 167)"
              color="white"
              _hover={{ bg: "rgb(27, 67, 137)" }}
              _active={{ bg: "rgb(17, 47, 107)" }}
              size="lg" 
              onClick={handleStartAssessment}
              mt={4}
            >
              Commencer l'évaluation
            </Button>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default StartEvaluation;
